import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import { articles } from "@/mocks/articles";
import { categories, siteInfo } from "@/mocks/site";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import SEOMeta from "@/components/base/SEOMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [collected, setCollected] = useState(false);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <SEOMeta
        title={`${article.title} - ${siteInfo.name}`}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={`https://mogutv.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogImage={article.coverImage}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            datePublished: article.publishDate,
            author: {
              "@type": "Person",
              name: article.author,
            },
            publisher: {
              "@type": "Organization",
              name: siteInfo.name,
            },
            url: `https://mogutv.com/article/${article.slug}`,
            inLanguage: "zh-CN",
            wordCount: article.content.length,
            timeRequired: `PT${article.readTime}M`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://mogutv.com/" },
              {
                "@type": "ListItem",
                position: 2,
                name: categoryInfo?.name || "分类",
                item: `https://mogutv.com/category/${article.category}`,
              },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Main content */}
              <article className="flex-1 min-w-0">
                <Breadcrumb
                  items={[
                    { label: "首页", href: "/" },
                    ...(categoryInfo
                      ? [{ label: categoryInfo.name, href: `/category/${article.category}` }]
                      : []),
                    { label: article.title },
                  ]}
                />

                {/* Cover */}
                <div className="rounded-xl overflow-hidden mb-8 aspect-[16/9] md:aspect-[21/9]">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Header meta */}
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6 pb-6 border-b border-background-200/70">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i> {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i> {article.readTime} 分钟阅读
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()} 次阅读
                  </span>
                  {categoryInfo && (
                    <Link
                      to={`/category/${article.category}`}
                      className="px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-xs font-medium hover:bg-primary-100 transition-colors whitespace-nowrap"
                    >
                      {categoryInfo.name}
                    </Link>
                  )}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-8">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Content */}
                <div
                  className="prose prose-lg max-w-none text-foreground-800 leading-relaxed
                    [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-heading
                    [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-800 [&_h3]:mt-8 [&_h3]:mb-3
                    [&_p]:mb-5 [&_p]:leading-relaxed [&_p]:text-foreground-700
                    [&_strong]:text-foreground-900 [&_strong]:font-semibold
                    [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-5 [&_ul]:space-y-1 [&_li]:text-foreground-700"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* Actions */}
                <div className="flex items-center gap-4 mt-10 pt-6 border-t border-background-200/70">
                  <button
                    onClick={() => {
                      setLiked(!liked);
                      setLikeCount((c) => (liked ? c - 1 : c + 1));
                    }}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                      liked
                        ? "bg-accent-50 text-accent-600 border border-accent-200"
                        : "bg-background-100 text-foreground-600 border border-background-200 hover:border-accent-200"
                    }`}
                  >
                    <i className={`${liked ? "ri-heart-fill text-accent-500" : "ri-heart-line"} text-lg`}></i>
                    点赞 {liked ? `(${article.viewCount > 0 ? Math.floor(article.viewCount * 0.05) + likeCount : likeCount})` : `(${Math.floor(article.viewCount * 0.05)})`}
                  </button>
                  <button
                    onClick={() => setCollected(!collected)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                      collected
                        ? "bg-secondary-50 text-secondary-600 border border-secondary-200"
                        : "bg-background-100 text-foreground-600 border border-background-200 hover:border-secondary-200"
                    }`}
                  >
                    <i className={`${collected ? "ri-bookmark-fill text-secondary-500" : "ri-bookmark-line"} text-lg`}></i>
                    {collected ? "已收藏" : "收藏"}
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                    }}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-background-100 text-foreground-600 border border-background-200 hover:border-primary-200 transition-all duration-200 cursor-pointer"
                  >
                    <i className="ri-share-forward-line text-lg"></i>
                    分享
                  </button>
                </div>
              </article>

              {/* Sidebar */}
              <aside className="w-full lg:w-80 flex-shrink-0">
                {/* Author card */}
                <div className="bg-background-100 rounded-xl p-5 mb-6 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-user-3-line text-xl text-primary-600"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-foreground-900 text-sm">{article.author}</div>
                      <div className="text-xs text-foreground-500">蘑菇tv认证创作者</div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    专注蘑菇tv内容创作领域，持续输出高质量教程与资源分享。
                  </p>
                </div>

                {/* Related articles */}
                {relatedArticles.length > 0 && (
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                    <h3 className="font-semibold text-foreground-900 mb-4 text-sm">
                      相关推荐
                    </h3>
                    <div className="space-y-4">
                      {relatedArticles.map((ra) => (
                        <Link
                          key={ra.slug}
                          to={`/article/${ra.slug}`}
                          className="flex gap-3 group"
                        >
                          <div className="w-20 h-14 flex-shrink-0 rounded-lg overflow-hidden">
                            <img
                              src={ra.coverImage}
                              alt={ra.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                              {ra.title}
                            </h4>
                            <span className="text-xs text-foreground-500 mt-1 block">
                              {ra.publishDate}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </aside>
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}